import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { collection, query, orderBy, limit, getDocs, getFirestore } from 'firebase/firestore';
import { useInView, reveal } from './motion';
import { pickRandomSample } from '../../lib/sampleUseCases';
import { useT, useLang } from '../../i18n/LanguageContext';

const FETCH_LIMIT = 20;
const SHOW_COUNT = 4;
const MIN_TO_SHOW = 3;

export default function UseCaseShowcase() {
  const theme = useTheme();
  const { ref, visible } = useInView();
  const t = useT();
  const { lang } = useLang();
  const [cases, setCases] = useState(null); // null = loading, [] = loaded-but-empty

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(getFirestore(), 'publicUseCases'),
          orderBy('createdAt', 'desc'),
          limit(FETCH_LIMIT),
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setCases(pickRandomSample(docs, SHOW_COUNT));
      } catch (e) {
        console.warn('UseCaseShowcase: fetch failed (non-fatal):', e);
        if (!cancelled) setCases([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cold start / not enough moderated content yet: render nothing, not a
  // half-empty or padded section (spec requirement — never look sparse).
  if (!cases || cases.length < MIN_TO_SHOW) return null;

  return (
    <Box sx={{ bgcolor: theme.home.paper, py: theme.home.sectionPy }}>
      <Box ref={ref} sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 } }}>
        <Typography sx={{
          fontFamily: theme.home.display, fontWeight: 800, textAlign: 'center',
          fontSize: { xs: '1.6rem', md: '2.2rem' }, color: theme.home.ink, mb: { xs: 4, md: 6 },
          ...reveal(visible, 0),
        }}>
          {t('home.useCaseShowcase.heading')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
          {cases.map((c, i) => (
            <Box key={c.id} sx={{
              p: 3, borderRadius: 3, bgcolor: theme.home.paperDeep, ...reveal(visible, 0.1 + i * 0.08),
            }}>
              {c.sector && <Chip size="small" label={c.sector} sx={{ mb: 1.5 }} />}
              <Typography sx={{ color: theme.home.ink, lineHeight: 1.5 }}>
                {(lang === 'it' ? c.anonymizedSummary : c[`anonymizedSummary_${lang}`] || c.anonymizedSummary)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
